import React, { useState, useEffect } from 'react';
import { Team, Player, Assessment, FAScores, Report } from '../types';
import { reportService, DateRange } from '../services/reportService';
import { assessmentService } from '../services/assessmentService';
import { BarChart } from './charts/BarChart';
import { Loader2, FileText, Download } from 'lucide-react';

const FA_MODEL = {
  technical: { title: 'Technical', color: '#ef4444', bgClass: 'bg-red-500', short: 'T' },
  physical: { title: 'Physical', color: '#eab308', bgClass: 'bg-yellow-500', short: 'P' },
  psychological: { title: 'Psychological', color: '#10b981', bgClass: 'bg-emerald-500', short: 'M' },
  social: { title: 'Social', color: '#3b82f6', bgClass: 'bg-blue-500', short: 'S' },
};

interface TeamAnalyticsDashboardProps { team: Team; players: Player[] }
interface PlayerAnalytics {
  player: Player; latestAssessment?: Assessment; assessmentCount: number;
  averageScore: number; cornerAverages: Record<string, number>; improvement: number;
}

export const TeamAnalyticsDashboard: React.FC<TeamAnalyticsDashboardProps> = ({ team, players }) => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [sortBy, setSortBy] = useState<'name' | 'score' | 'improvement'>('name');
  const [playerAnalytics, setPlayerAnalytics] = useState<PlayerAnalytics[]>([]);
  const [teamReports, setTeamReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => { loadTeamAnalytics(); loadTeamReports(); }, [team.id, players]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTeamAnalytics = async () => {
    try {
      setIsLoading(true);
      const analytics: PlayerAnalytics[] = [];
      for (const player of players) {
        try {
          const assessments = await assessmentService.getPlayerAssessments(player.id);
          const latest = assessments[0]; const prev = assessments[1];
          let averageScore = 0; let cornerAverages: Record<string, number> = {}; let improvement = 0;
          if (latest) {
            const all: number[] = [];
            Object.values(latest.scores).forEach(c => Object.values(c as Record<string, number>).forEach(s => all.push(s)));
            averageScore = all.reduce((s, v) => s + v, 0) / all.length;
            Object.entries(FA_MODEL).forEach(([k]) => {
              const cs = latest.scores[k as keyof FAScores] as Record<string, number>;
              const vals = Object.values(cs);
              cornerAverages[k] = vals.reduce((s, v) => s + v, 0) / vals.length;
            });
            if (prev) {
              const pa: number[] = [];
              Object.values(prev.scores).forEach(c => Object.values(c as Record<string, number>).forEach(s => pa.push(s)));
              improvement = averageScore - pa.reduce((s, v) => s + v, 0) / pa.length;
            }
          }
          analytics.push({ player, latestAssessment: latest, assessmentCount: assessments.length, averageScore, cornerAverages, improvement });
        } catch { analytics.push({ player, assessmentCount: 0, averageScore: 0, cornerAverages: {}, improvement: 0 }); }
      }
      setPlayerAnalytics(analytics);
    } catch { /* ignore */ } finally { setIsLoading(false); }
  };

  const loadTeamReports = async () => { try { setTeamReports(await reportService.getTeamReports(team.id)); } catch { /* ignore */ } };

  const handleGenerateTeamReport = async () => {
    try { setIsGenerating(true); const r = await reportService.generateTeamReport(team.id, { dateRange }); setTeamReports([r, ...teamReports]); }
    catch { /* ignore */ } finally { setIsGenerating(false); }
  };

  const handleDownloadReport = async (id: string) => { try { await reportService.downloadReport(id); } catch { /* ignore */ } };

  const fmtDate = (d: Date | string) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const teamMetrics = (() => {
    const assessed = playerAnalytics.filter(p => p.assessmentCount > 0);
    if (!assessed.length) return { averageScore: 0, totalAssessments: 0, playersAssessed: 0, averageImprovement: 0 };
    return {
      averageScore: assessed.reduce((s, p) => s + p.averageScore, 0) / assessed.length,
      totalAssessments: playerAnalytics.reduce((s, p) => s + p.assessmentCount, 0),
      playersAssessed: assessed.length,
      averageImprovement: assessed.reduce((s, p) => s + p.improvement, 0) / assessed.length,
    };
  })();

  const positions = Array.from(new Set(players.map(p => p.position))).sort();

  const filtered = (() => {
    let list = selectedPosition === 'all' ? playerAnalytics : playerAnalytics.filter(p => p.player.position === selectedPosition);
    list.sort((a, b) => sortBy === 'score' ? b.averageScore - a.averageScore : sortBy === 'improvement' ? b.improvement - a.improvement : `${a.player.firstName}`.localeCompare(`${b.player.firstName}`));
    return list;
  })();

  const cornerComparisonData = Object.entries(FA_MODEL).map(([k, cfg]) => {
    const vals = playerAnalytics.filter(p => p.cornerAverages[k]).map(p => p.cornerAverages[k]);
    return { label: cfg.short, value: vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0, color: cfg.color };
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Team Analytics: {team.name}</h2>
            <p className="text-sm text-gray-500 mt-1">{players.length} players · {teamMetrics.totalAssessments} total assessments</p>
          </div>
          <button onClick={handleGenerateTeamReport} disabled={isGenerating || teamMetrics.totalAssessments === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]">
            {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {isGenerating ? 'Generating...' : 'Generate Team Report'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
            <select value={selectedPosition} onChange={e => setSelectedPosition(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none min-h-[44px]">
              <option value="all">All Positions</option>
              {positions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value as any)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none min-h-[44px]">
              <option value="name">Name</option>
              <option value="score">Average Score</option>
              <option value="improvement">Improvement</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input type="date" value={dateRange?.startDate ? dateRange.startDate.toISOString().split('T')[0] : ''}
              onChange={e => setDateRange(p => ({ ...p, startDate: new Date(e.target.value) } as DateRange))}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none min-h-[44px]" />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input type="date" value={dateRange?.endDate ? dateRange.endDate.toISOString().split('T')[0] : ''}
              onChange={e => setDateRange(p => ({ ...p, endDate: new Date(e.target.value) } as DateRange))}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none min-h-[44px]" />
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{teamMetrics.averageScore.toFixed(1)}</p>
          <p className="text-sm text-gray-500 mt-1">Team Average</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{teamMetrics.playersAssessed}</p>
          <p className="text-sm text-gray-500 mt-1">Players Assessed</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{teamMetrics.totalAssessments}</p>
          <p className="text-sm text-gray-500 mt-1">Total Assessments</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className={`text-2xl font-bold ${teamMetrics.averageImprovement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {teamMetrics.averageImprovement >= 0 ? '+' : ''}{teamMetrics.averageImprovement.toFixed(1)}
          </p>
          <p className="text-sm text-gray-500 mt-1">Avg Improvement</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills Category Comparison</h3>
          {teamMetrics.totalAssessments > 0 ? (
            <BarChart data={cornerComparisonData} height={300} yMin={0} yMax={10} />
          ) : (
            <div className="h-64 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-sm text-gray-400">Complete assessments to see comparison</p>
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Position Analysis</h3>
          {playerAnalytics.some(p => p.averageScore > 0) ? (
            <BarChart data={playerAnalytics.filter(p => p.averageScore > 0).reduce((acc, { player, averageScore }) => {
              const existing = acc.find(a => a.label === player.position);
              if (existing) { existing.value = (existing.value + averageScore) / 2; }
              else { acc.push({ label: player.position, value: averageScore, color: '#10b981' }); }
              return acc;
            }, [] as { label: string; value: number; color: string }[])} height={300} yMin={0} yMax={10} />
          ) : (
            <div className="h-64 bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-sm text-gray-400">Complete assessments to see position analysis</p>
            </div>
          )}
        </div>
      </div>

      {/* Player Cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-gray-500">No player data available. Complete assessments to see analytics.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Player Analysis ({filtered.length} players)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(({ player, assessmentCount, averageScore, cornerAverages, improvement, latestAssessment }) => (
              <div key={player.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-gray-900">{player.firstName} {player.lastName}</p>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{player.position}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-gray-900">{averageScore.toFixed(1)}</p>
                    <p className="text-xs text-gray-500">Avg Score</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-gray-900">{assessmentCount}</p>
                    <p className="text-xs text-gray-500">Assessments</p>
                  </div>
                </div>
                {latestAssessment && (
                  <div className="grid grid-cols-4 gap-1">
                    {Object.entries(FA_MODEL).map(([k, cfg]) => (
                      <div key={k} className="text-center p-1.5 rounded" style={{ backgroundColor: `${cfg.color}15`, border: `1px solid ${cfg.color}30` }}>
                        <p className="text-sm font-semibold" style={{ color: cfg.color }}>{(cornerAverages[k] || 0).toFixed(1)}</p>
                        <p className="text-[10px] text-gray-500 uppercase">{cfg.short}</p>
                      </div>
                    ))}
                  </div>
                )}
                {improvement !== 0 && (
                  <p className={`text-xs font-medium mt-2 ${improvement > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {improvement > 0 ? '+' : ''}{improvement.toFixed(1)} improvement
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Reports */}
      {teamReports.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Team Reports</h3>
          <div className="space-y-3">
            {teamReports.slice(0, 3).map(report => {
              const expired = reportService.isReportExpired(report);
              return (
                <div key={report.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{reportService.formatReportType(report.type)} — {fmtDate(report.createdAt)}</p>
                    <p className={`text-xs mt-0.5 ${expired ? 'text-red-500' : 'text-green-600'}`}>{expired ? 'Expired' : 'Available'}</p>
                  </div>
                  <button disabled={expired} onClick={() => handleDownloadReport(report.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]">
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
